import React, { useState } from 'react';
import { Mail, Loader2, Check, X } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

const HeaderNewsletter = () => {
  const { subscribe } = useSubscription();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) return;
    
    setStatus('loading');
    
    try {
      await subscribe(email);
      
      setStatus('success');
      setEmail('');
      
      // Reset after 3 seconds
      setTimeout(() => {
        setStatus('idle');
        setIsExpanded(false);
      }, 3000);
    } catch (error) {
      setStatus('error');
      setTimeout(() => {
        setStatus('idle');
      }, 3000);
    }
  };

  return (
    <div className="relative flex items-center">
      {/* Collapsed state - just icon button on mobile, full on desktop */}
      {!isExpanded && status !== 'success' && (
        <>
          {/* Mobile: Icon only */}
          <button
            onClick={() => setIsExpanded(true)}
            className="md:hidden flex items-center justify-center p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all border border-white/20"
            data-testid="header-newsletter-toggle"
          >
            <Mail className="w-5 h-5 text-white" />
          </button>
          
          {/* Desktop: Full input bar */}
          <form onSubmit={handleSubmit} className="hidden md:flex items-center gap-2">
            <div className="relative flex items-center">
              <Mail className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Get weekly updates"
                data-testid="header-newsletter-email"
                className="pl-9 pr-4 py-2 w-44 lg:w-52 rounded-full bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400 font-secondary focus:outline-none focus:border-white/40 transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={!email || status === 'loading'}
              data-testid="header-newsletter-submit"
              className="px-4 py-2 bg-[#5DADE2] text-black hover:bg-[#85C1E9] font-secondary font-bold text-sm rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {status === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Subscribe'
              )}
            </button>
          </form>
        </>
      )}

      {/* Expanded state on mobile - dropdown style */}
      {isExpanded && status !== 'success' && (
        <>
          {/* Backdrop */}
          <div 
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsExpanded(false)}
          />
          
          {/* Dropdown form */}
          <div className="md:hidden fixed top-16 left-0 right-0 z-50 px-4 py-3 bg-black/95 backdrop-blur-xl border-b border-white/10 shadow-xl">
            <form onSubmit={handleSubmit} className="flex items-center gap-3 max-w-md mx-auto">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  autoFocus
                  className="w-full pl-10 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white text-base placeholder-gray-400 font-secondary focus:outline-none focus:border-white/40"
                />
              </div>
              <button
                type="submit"
                disabled={!email || status === 'loading'}
                className="px-5 py-3 bg-[#5DADE2] text-black rounded-full transition-all disabled:opacity-50 font-secondary font-bold"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Subscribe'
                )}
              </button>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="p-2 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </form>
          </div>
        </>
      )}

      {/* Success state */}
      {status === 'success' && (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/40 rounded-full">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-sm font-secondary">Subscribed!</span>
        </div>
      )}
    </div>
  );
};

export default HeaderNewsletter;
